import {
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { TriggerType, TRIGGER_STATUS } from './TriggerType';
import { ScheduleTriggersDialog } from '@/modules/automation-details/components/schedules/ScheduleTriggersDialog';
import type { IScheduleTriggerProps } from './types';
import { usePermissions } from '@/shared/permissions';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

/**
 * ScheduleTrigger Component
 *
 * Handles the schedule trigger type selection and configuration.
 * Encapsulates the schedule dialog for configuring recurring automation runs.
 *
 * Flow:
 * 1. User clicks the schedule trigger type button
 * 2. Opens ScheduleTriggersDialog for configuration
 * 3. User configures the schedule (frequency, days, times)
 * 4. On save, creates schedule for the automation
 *
 * @param props - Component props
 * @returns TriggerType component for schedule with integrated dialog
 */
export function ScheduleTrigger({
  automationId,
  disabled,
  hasSchedule,
  isEnabled,
  description,
  presentation = 'card',
  onOpen,
  open,
  onOpenChange,
  hideTrigger = false,
}: IScheduleTriggerProps) {
  const { t } = useTranslation('automation-inputs');
  const { hasPermission } = usePermissions();

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Check if can create schedule.
  const canCreateSchedule = hasPermission(['CreateAutomationSchedule'], {
    organization: currentOrganizationId,
    workspace: currentWorkspaceId,
  });

  const {
    value: isScheduleDialogOpen,
    setTrue: openScheduleDialog,
    setValue: setIsScheduleDialogOpen,
  } = useBoolean(false);

  // Determine status badge: active if enabled, paused if disabled, undefined if no schedule
  const getStatus = () => {
    if (!hasSchedule) return undefined;
    return isEnabled ? TRIGGER_STATUS.ACTIVE : TRIGGER_STATUS.PAUSED;
  };

  const handleOpen = () => {
    onOpen?.();
    openScheduleDialog();
    onOpenChange?.(true);
  };

  const triggerTypeCard = !hideTrigger ? (
    <TriggerType
      icon={
        <Icon
          type="Calendar"
          size="lg"
          strokeWidth={1.5}
          className="text-ring"
        />
      }
      title={
        hasSchedule
          ? t('triggerTypes.activeSchedule.title')
          : t('triggerTypes.schedule.title')
      }
      description={description ?? t('triggerTypes.schedule.description')}
      onClick={disabled ? undefined : handleOpen}
      disabled={disabled || !canCreateSchedule}
      status={getStatus()}
      presentation={presentation}
    />
  ) : null;

  return (
    <>
      {!canCreateSchedule ? (
        <Tooltip variant="primary">
          <TooltipTrigger asChild>
            <div>{triggerTypeCard}</div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-sm">
            {t('triggerTypes.schedule.createDisabledTooltip')}
          </TooltipContent>
        </Tooltip>
      ) : disabled ? (
        <Tooltip variant="primary">
          <TooltipTrigger asChild>
            <div>{triggerTypeCard}</div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {t('triggerTypes.schedule.disabledTooltip')}
          </TooltipContent>
        </Tooltip>
      ) : (
        triggerTypeCard
      )}

      <ScheduleTriggersDialog
        automationId={automationId}
        open={open ?? isScheduleDialogOpen}
        onOpenChange={nextOpen => {
          setIsScheduleDialogOpen(nextOpen);
          onOpenChange?.(nextOpen);
        }}
      />
    </>
  );
}
